import { IProducts } from '@llws/platform-shared-types/types/db/auth';
import { IStateStrict } from '@/shared/interfaces/store-base';
import { productsGroupMap } from '@/shared/helpers/productGroupsMap';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

const premiumProductKeysBlacklist: string[] = [
  'interactive_voice_responses',
];

export interface IProductType {
  name: string;
  navKey: string;
  modelType: string[];
  productType: string[];
}
interface IProductTypeMap {
  account: IProductType;
  property : IProductType;
  propertyPremium : IProductType;
  website : IProductType;
}

interface IProductWithGroups {
  product: IProducts;
  group: string;
}

const propertyProductGroupsMap: Record<string, string[]> = {
  propertyManagement: ['buildings', 'building_photos', 'units', 'unit_types', 'quick_update', 'maintenance'],
  listingManagement: ['listing_scores', 'promoted_listings', 'promotions', 'standard_listings'],
  leads: ['lead_tracker', 'waitinglist', 'leads_reporting'],
  reporting: ['listing_scores_reporting', 'tours_reporting', 'digital_marketing_reporting', 'floorplan_navigator_reporting'],
  general: [],
};

const findGroup = (key: string | undefined) => Object.keys(propertyProductGroupsMap).find((groupKey: string) => propertyProductGroupsMap[groupKey].includes(key ?? '')) ?? 'general';

/**
 * Replace Products with their groups
 * @param products The product list
 * @returns A list with products replaced by their groups
 */
const groupProducts = (
  products: Partial<IProducts>[],
): Partial<IProducts>[] => products.reduce((preV: Partial<IProducts>[], curV) => {
  const key = Object.keys(productsGroupMap).find((k) => productsGroupMap[k].list.includes(curV.key ?? ''));
  if (!key) {
    // not in a group
    return [ ...preV, curV];
  }
  if (preV.findIndex((item) => item.key === key) < 0) {
    // in a group but not grouped yet
    // should be replaced by its group
    const group = productsGroupMap[key];
    return [ ...preV, {
      ...curV,
      key,
      name: group.name,
    }];
  }
  // already groupd
  return preV;
}, [] as Partial<IProducts>[]);

export const productTypes: IProductTypeMap = {
  account: {
    name: 'Account',
    navKey: 'account',
    modelType: ['account'],
    productType: ['basic', 'admin', 'feature'],
  },
  property: {
    name: 'All Properties',
    navKey: 'property',
    modelType: ['building'],
    productType: ['basic'],
  },
  propertyPremium: {
    name: 'Premium Property Features',
    navKey: 'propertyPremium',
    modelType: ['building'],
    productType: ['premium'],
  },
  website: {
    name: 'Website',
    navKey: 'website',
    modelType: ['site'],
    productType: ['basic'],
  },
};

interface IPermsApiProductsState extends IStateStrict<Partial<IProducts>> {
  accountProducts: IProducts[];
  propertyProducts: IProducts[];
  propertyProductsWithGroups: IProductWithGroups[];
  propertyPremiumProducts: IProducts[];
  websiteProducts: IProducts[];
  groupedAccountProducts: IProducts[];
  groupedPropertyProducts: IProducts[];
  groupedPropertyPremiumProducts: IProducts[];
  groupedWebsiteProducts: IProducts[];
}

const base = new StoreBaseImplementationV2<Partial<IProducts>>({
  adminGatewayResourcePath: '/perms-api/products',
});

export default {
  namespaced: true,

  state: {
    ...base.state,

    accountProducts: [],
    propertyProducts: [],
    propertyPremiumProducts: [],
    websiteProducts: [],

    groupedAccountProducts: [],
    groupedPropertyProducts: [],
    groupedPropertyPremiumProducts: [],
    groupedWebsiteProducts: [],

    propertyProductsWithGroups: [],
  },

  getters: {
    ...base.getters,
    accountProducts: (state: IPermsApiProductsState) => (state.list?.data ?? []).filter((product) => productTypes.account.modelType.includes(`${product?.modelType}`) && productTypes.account.productType.includes(`${product?.productType}`)) ?? [],
    propertyProducts: (state: IPermsApiProductsState) => (state.list?.data ?? []).filter((product) => productTypes.property.modelType.includes(`${product?.modelType}`) && productTypes.property.productType.includes(`${product?.productType}`)) ?? [],
    propertyPremiumProducts: (state: IPermsApiProductsState) => (state.list?.data ?? []).filter((product) => productTypes.propertyPremium.modelType.includes(`${product?.modelType}`) && productTypes.propertyPremium.productType.includes(`${product?.productType}`)) ?? [],
    websiteProducts: (state: IPermsApiProductsState) => (state.list?.data ?? []).filter((product) => productTypes.website.modelType.includes(`${product?.modelType}`) && productTypes.website.productType.includes(`${product?.productType}`)) ?? [],

    groupedAccountProducts: (state: IPermsApiProductsState) => groupProducts(state.list?.data ?? []).filter((product) => productTypes.account.modelType.includes(`${product?.modelType}`) && productTypes.account.productType.includes(`${product?.productType}`)) ?? [],

    groupedPropertyProducts: (state: IPermsApiProductsState) => groupProducts(state.list?.data ?? []).filter((product) => productTypes.property.modelType.includes(`${product?.modelType}`) && productTypes.property.productType.includes(`${product?.productType}`)) ?? [],

    groupedPropertyPremiumProducts: (state: IPermsApiProductsState) => groupProducts(state.list?.data ?? []).filter((product) => (
      productTypes.propertyPremium.modelType.includes(`${product?.modelType}`)
        && productTypes.propertyPremium.productType.includes(`${product?.productType}`)
        && !premiumProductKeysBlacklist.includes(`${product.key}`)
    )),

    groupedWebsiteProducts: (state: IPermsApiProductsState) => groupProducts(state.list?.data ?? []).filter((product) => productTypes.website.modelType.includes(`${product?.modelType}`) && productTypes.website.productType.includes(`${product?.productType}`)) ?? [],

    propertyProductsWithGroups: (state: IPermsApiProductsState) => groupProducts(state.list?.data ?? []).filter(
      (product) => productTypes.property.modelType.includes(`${product?.modelType}`) && productTypes.property.productType.includes(`${product?.productType}`),
    ).map((product) => ({ ...product, group: findGroup(product.key) })) ?? [],

  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,
  },
};
